// // AttachmentDialog.jsx
// import React, { useState } from 'react';
// import { X, Paperclip, Upload } from 'lucide-react';
// import api from '../../services/api';

// const AttachmentDialog = ({ isOpen, onClose, attachments, setAttachments }) => {
//   const [uploading, setUploading] = useState(false);
//   const [error, setError] = useState('');

//   const handleFileUpload = async (event) => {
//     const file = event.target.files[0];
//     if (!file) return;

//     setUploading(true);
//     setError('');

//     try {
//       const formData = new FormData();
//       formData.append('file', file);

//       const response = await api.post('/schedule_email/upload-image', formData, {
//         headers: {
//           'Content-Type': 'multipart/form-data',
//         },
//       });

//       // Add the uploaded file to attachments
//       setAttachments([
//         ...attachments,
//         {
//           name: file.name,
//           link: response.data.image_url,
//         },
//       ]);

//       // Reset file input
//       event.target.value = '';
//     } catch (err) {
//       console.error('Upload failed:', err);
//       setError('Failed to upload file. Please try again.');
//     } finally {
//       setUploading(false);
//     }
//   };

//   const handleRemoveAttachment = (indexToRemove) => {
//     setAttachments(attachments.filter((_, index) => index !== indexToRemove));
//   };

//   if (!isOpen) return null;

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-white rounded-lg shadow-xl w-[400px] flex flex-col">
//         {/* Header */}
//         <div className="flex items-center justify-between p-4 border-b">
//           <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
//             <Paperclip className="w-5 h-5" />
//             Attachments
//           </h2>
//           <button
//             onClick={onClose}
//             className="text-gray-500 hover:text-gray-700 transition"
//           >
//             <X className="w-5 h-5" />
//           </button>
//         </div>

//         {/* Content */}
//         <div className="flex-1 overflow-y-auto p-4">
//           {/* Attach Button */}
//           <div className="mb-4">
//             <label
//               htmlFor="file-upload"
//               className={`flex items-center justify-center gap-2 w-full px-4 py-3 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600 transition ${
//                 uploading ? 'opacity-50 cursor-not-allowed' : ''
//               }`}
//             >
//               <Upload className="w-5 h-5" />
//               {uploading ? 'Uploading...' : 'Attach File'}
//             </label>
//             <input
//               id="file-upload"
//               type="file"
//               onChange={handleFileUpload}
//               disabled={uploading}
//               className="hidden"
//             />
//           </div>

//           {/* Error Message */}
//           {error && (
//             <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
//               {error}
//             </div>
//           )}

//           {/* Attachments List */}
//           <div className="space-y-2">
//             {attachments.length === 0 ? (
//               <p className="text-gray-500 text-center py-8">No attachments yet</p>
//             ) : (
//               attachments.map((attachment, index) => (
//                 <div
//                   key={index}
//                   className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition group"
//                 >
//                   <div className="flex items-center gap-3 flex-1 min-w-0">
//                     <Paperclip className="w-4 h-4 text-gray-500 flex-shrink-0" />
//                     <div className="flex-1 min-w-0">
//                       <p className="text-sm font-medium text-gray-800 truncate">
//                         {attachment.name}
//                       </p>
//                       <a
//                         href={attachment.link}
//                         target="_blank"
//                         rel="noopener noreferrer"
//                         className="text-xs text-blue-500 hover:underline truncate block"
//                       >
//                         {attachment.link}
//                       </a>
//                     </div>
//                   </div>
//                   <button
//                     onClick={() => handleRemoveAttachment(index)}
//                     className="ml-2 p-1 text-gray-400 hover:text-red-500 transition flex-shrink-0"
//                     title="Remove attachment"
//                   >
//                     <X className="w-4 h-4" />
//                   </button>
//                 </div>
//               ))
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AttachmentDialog;


// AttachmentDialog.jsx
import React, { useState } from 'react';
import { X, Paperclip, Upload, AlertCircle } from 'lucide-react';
import axios from 'axios';
import api from '../../services/api';

const AttachmentDialog = ({ isOpen, onClose, attachments, setAttachments }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  
  // Gmail limits
  const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25 MB in bytes
  const SAFE_MAX_SIZE = 12.5 * 1024 * 1024; // 12.5 MB (accounting for encoding)

  // Calculate current total size of attachments
  const calculateTotalSize = () => {
    return attachments.reduce((total, attachment) => {
      return total + (attachment.size || 0);
    }, 0);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError('');

    // Check individual file size
    if (file.size > SAFE_MAX_SIZE) {
      setError(`File size (${formatFileSize(file.size)}) exceeds the safe limit of 12.5 MB. Gmail may reject files larger than this due to encoding.`);
      event.target.value = '';
      return;
    }

    // Check total size including new file
    const currentTotalSize = calculateTotalSize();
    const newTotalSize = currentTotalSize + file.size;
    
    if (newTotalSize > SAFE_MAX_SIZE) {
      setError(`Adding this file would exceed Gmail's 12.5 MB safe limit. Current total: ${formatFileSize(currentTotalSize)}, File size: ${formatFileSize(file.size)}`);
      event.target.value = '';
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/schedule_email/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Add the uploaded file to attachments with size info
      setAttachments([
        ...attachments,
        {
          name: file.name,
          link: response.data.image_url,
          size: file.size,
        },
      ]);

      // Reset file input
      event.target.value = '';
    } catch (err) {
      console.error('Upload failed:', err);
      setError(`Upload failed: ${err}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = async (indexToRemove) => {
    const attachment = attachments[indexToRemove];

    // Extract filename from the URL (last path segment)
    const fileName = attachment.link?.split('/').pop();

    if (fileName) {
      try {
        await api.delete(`/schedule_email/delete-image/${fileName}`);
      } catch (err) {
        console.error('Failed to delete file from server:', err);
        setError('Failed to delete file from server. Removing from attachment anyway.');
      }
    }

    setAttachments(attachments.filter((_, index) => index !== indexToRemove));
  };

  if (!isOpen) return null;

  const totalSize = calculateTotalSize();
  const remainingSize = SAFE_MAX_SIZE - totalSize;
  const percentageUsed = (totalSize / SAFE_MAX_SIZE) * 100;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[400px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Paperclip className="w-5 h-5" />
            Attachments
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Size Indicator */}
        <div className="px-4 pt-4 pb-2">
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-gray-600">
              <span>Total Size: {formatFileSize(totalSize)}</span>
              <span>Limit: 12.5 MB</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  percentageUsed > 90
                    ? 'bg-red-500'
                    : percentageUsed > 70
                    ? 'bg-yellow-500'
                    : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(percentageUsed, 100)}%` }}
              />
            </div>
            <p className="text-xs text-gray-500">
              {formatFileSize(remainingSize)} remaining
            </p>
          </div>
        </div>

        {/* Gmail Limit Warning */}
        <div className="px-4 pb-2">
          <div className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">
              Gmail has a 25 MB limit per email, but encoding reduces the safe limit to ~12.5 MB
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {/* Attach Button */}
          <div className="mb-4">
            <label
              htmlFor="file-upload"
              className={`flex items-center justify-center gap-2 w-full px-4 py-3 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600 transition ${
                uploading || remainingSize <= 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Upload className="w-5 h-5" />
              {uploading ? 'Uploading...' : 'Attach File'}
            </label>
            <input
              id="file-upload"
              type="file"
              onChange={handleFileUpload}
              disabled={uploading || remainingSize <= 0}
              className="hidden"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Attachments List */}
          <div className="space-y-2">
            {attachments.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No attachments yet</p>
            ) : (
              attachments.map((attachment, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition group"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Paperclip className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {attachment.name}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{formatFileSize(attachment.size || 0)}</span>
                        <a
                          href={attachment.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline truncate"
                        >
                          View
                        </a>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveAttachment(index)}
                    className="ml-2 p-1 text-gray-400 hover:text-red-500 transition flex-shrink-0"
                    title="Remove attachment"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachmentDialog;
